import type { AxiosError } from 'axios';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import styled from '@emotion/styled';
import {
  Modal,
  Button,
  Field,
  TextInput,
  Select,
  Stack,
  Loader,
  useNotify,
} from '@hotjar/hotsauce';
import type { OrganizationAccess } from '@hotjar/organization';
import { ACCESS_CHOICES } from '@hotjar/webapp/constants/accessChoices';
import { getParameter } from '@hotjar/insights-common/lib/url';
import { validateEmail } from '@hotjar/webapp/lib/email';
import { invite } from '@hotjar/organization';
import { useAccessRequest } from './useAccessRequest';

const Form = styled.form``;

interface GrantAccessFormData {
  email: string;
  access: OrganizationAccess;
}

export const GrantAccessModal = (): React.ReactElement | null => {
  const token = getParameter(window.location.href, 'request_access_token');
  const { t } = useTranslation();
  const { data, isLoading, isError, error } = useAccessRequest(token);
  const { email, accountId, organizationId, organizationName } = data || {};
  const [isOpen, setOpen] = useState(!!token);
  const [isProcessing, setIsProcessing] = useState(false);
  const { showToast } = useNotify();
  const { register, handleSubmit, errors } = useForm<GrantAccessFormData>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
  });

  const onSubmit = (invitation: GrantAccessFormData) => {
    if (organizationId && accountId) {
      setIsProcessing(true);
      invite(accountId, organizationId, [invitation]).then(({ success }) => {
        if (success) {
          setOpen(false);
          showToast({ text: t('activation:grant-access.toasts.success'), kind: 'success' });
        } else {
          showToast({ text: t('activation:grant-access.toasts.error'), kind: 'danger' });
          setIsProcessing(false);
        }
      });
    }
  };

  useEffect(() => {
    if (isError) {
      const inlineError = (error as AxiosError)?.response?.data?.error || 'invalid-token';
      setOpen(false);
      showToast({ text: t(`activation:grant-access.toasts.${inlineError}`), kind: 'danger' });
    }
  }, [isError, showToast, t, error]);

  if (!token || isLoading) return null;

  return (
    <Modal
      contentLabel={t('activation:grant-access.title')}
      isOpen={isOpen}
      onClose={() => setOpen(false)}
    >
      <Form onSubmit={handleSubmit(onSubmit)} noValidate>
        <Modal.Header
          title={isLoading ? '' : t('activation:grant-access.title', { organizationName })}
        />
        <Modal.Body>
          {isLoading ? (
            <Loader />
          ) : (
            <Stack>
              <Field
                inputId="email"
                labelText={t('activation:grant-access.form.labels.email')}
                invalidText={errors.email?.message}
                isInvalid={!!errors.email}
                isRequired
              >
                <TextInput
                  id="email"
                  name="email"
                  placeholder="name@company.com"
                  defaultValue={email}
                  ref={
                    register &&
                    register({
                      required: t('activation:grant-access.form.errors.required') as string,
                      validate: (value: string) =>
                        validateEmail(value) ||
                        (t('activation:grant-access.form.errors.invalid-email') as string),
                    })
                  }
                />
              </Field>
              <Field
                labelText={t('activation:grant-access.form.labels.access')}
                inputId="access"
                invalidText={errors.access?.message}
                isInvalid={!!errors.access}
                isRequired
              >
                <Select
                  id="access"
                  name="access"
                  ref={
                    register &&
                    register({
                      required: t('activation:grant-access.form.errors.required') as string,
                    })
                  }
                >
                  {ACCESS_CHOICES.map(({ label, value }) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </Select>
              </Field>
            </Stack>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button kind="ghost" onClick={() => setOpen(false)} isLoading={isProcessing}>
            {t('activation:grant-access.buttons.cancel')}
          </Button>
          <Button kind="primary" isDisabled={isLoading} type="submit" isLoading={isProcessing}>
            {t('activation:grant-access.buttons.grant-access')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};
